import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft } from 'lucide-react';
import { cn } from './cn';

/**
 * Bottom sheet: what a dialog or popover is on the web. Large (`full`) for
 * forms and flows, medium (content height) otherwise. Drag the grabber or the
 * header down, or tap the scrim, to close; with `dirty` set, closing asks
 * first. Header is Cancel / title (+ subtitle) / action.
 */
export function Sheet({
  open,
  onClose,
  title,
  subtitle,
  leading,
  trailing,
  footer,
  full,
  dirty,
  children
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  leading?: ReactNode;
  trailing?: ReactNode;
  /** Pinned under the scroll area: a summary bar or the primary button. */
  footer?: ReactNode;
  full?: boolean;
  dirty?: boolean;
  children: ReactNode;
}) {
  const [drag, setDrag] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const start = useRef<number | null>(null);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const requestClose = () => (dirty ? setConfirming(true) : onClose());

  const handlers = {
    onTouchStart: (e: React.TouchEvent) => {
      start.current = e.touches[0].clientY;
    },
    onTouchMove: (e: React.TouchEvent) => {
      if (start.current !== null) setDrag(Math.max(0, e.touches[0].clientY - start.current));
    },
    onTouchEnd: () => {
      if (drag > 120) requestClose();
      start.current = null;
      setDrag(0);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 animate-[fade-in_200ms] bg-[var(--scrim)]" onClick={requestClose} />
      <div
        role="dialog"
        aria-modal="true"
        className={cn(
          'absolute inset-x-0 bottom-0 flex animate-[sheet-in_280ms_cubic-bezier(0.32,0.72,0,1)] flex-col rounded-t-[20px] bg-[var(--page)] shadow-2xl',
          full ? 'top-[calc(var(--sat)+10px)]' : 'max-h-[calc(100dvh-var(--sat)-10px)]'
        )}
        style={{ transform: drag ? `translateY(${drag}px)` : undefined, transition: drag ? 'none' : undefined }}
      >
        <div {...handlers} className="shrink-0 touch-none">
          <div className="mx-auto mt-1.5 h-[5px] w-9 rounded-full bg-zinc-300 dark:bg-zinc-600" />
          {(title || leading || trailing) && (
            <div className="grid h-12 grid-cols-[1fr_auto_1fr] items-center px-2">
              <div className="flex justify-start">{leading}</div>
              <div className="flex max-w-[200px] flex-col items-center">
                <span className="truncate text-body font-semibold">{title}</span>
                {subtitle && <span className="text-[12px] text-[var(--text-muted)]">{subtitle}</span>}
              </div>
              <div className="flex justify-end">{trailing}</div>
            </div>
          )}
        </div>
        <div className={cn('min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pt-2', footer ? 'pb-4' : 'pb-[calc(var(--sab)+16px)]')}>{children}</div>
        {footer && <div className="shrink-0 pb-[max(var(--sab),12px)]">{footer}</div>}
      </div>
      <ActionSheet
        open={confirming}
        onClose={() => setConfirming(false)}
        title="Discard your changes?"
        actions={[{ label: 'Discard changes', destructive: true, onSelect: onClose }]}
        cancelLabel="Keep editing"
      />
    </div>,
    document.body
  );
}

/** A back chevron for a pushed panel inside a sheet ("‹ New entry"). */
export function SheetBack({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="flex h-11 items-center gap-0.5 pr-2 text-body text-[var(--accent)] active:opacity-50">
      <ChevronLeft className="size-[22px]" strokeWidth={2.2} />
      <span className="max-w-[7.5rem] truncate">{label}</span>
    </button>
  );
}

export interface SheetAction {
  label: string;
  onSelect: () => void;
  destructive?: boolean;
}

/**
 * Action sheet: what a dropdown menu is on the web. Destructive actions go
 * last and red; Cancel is its own button underneath.
 */
export function ActionSheet({
  open,
  onClose,
  title,
  actions,
  cancelLabel = 'Cancel'
}: {
  open: boolean;
  onClose: () => void;
  title?: string;
  actions: SheetAction[];
  cancelLabel?: string;
}) {
  if (!open) return null;
  const ordered = [...actions.filter((a) => !a.destructive), ...actions.filter((a) => a.destructive)];
  return createPortal(
    <div className="fixed inset-0 z-[60]">
      <div className="absolute inset-0 animate-[fade-in_200ms] bg-[var(--scrim)]" onClick={onClose} />
      <div className="absolute inset-x-2 bottom-[calc(var(--sab)+8px)] flex animate-[sheet-in_260ms_cubic-bezier(0.32,0.72,0,1)] flex-col gap-2">
        <div className="group overflow-hidden rounded-[14px] bg-[var(--surface)]/97 backdrop-blur-xl" style={{ ['--inset' as string]: '0px' }}>
          {title && <div className="px-4 py-3.5 text-center text-caption text-[var(--text-muted)]">{title}</div>}
          {ordered.map((a) => (
            <button
              key={a.label}
              type="button"
              onClick={() => {
                onClose();
                a.onSelect();
              }}
              className={cn('h-14 w-full text-body active:bg-[var(--pressed)]', a.destructive ? 'text-[var(--danger)]' : 'text-[var(--accent)]')}
            >
              {a.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="h-14 w-full rounded-[14px] bg-[var(--surface)] text-body font-semibold text-[var(--accent)] active:bg-[var(--pressed)]"
        >
          {cancelLabel}
        </button>
      </div>
    </div>,
    document.body
  );
}
